import React, { useState } from 'react';
import NavigationBar from './NavigationBar';
import './Formatter.css';

interface FormatResult {
  formatted: string;
  explanation: string;
}

const Formatter: React.FC = () => {
  const [inputValue, setInputValue] = useState<string>('');
  const [selectedFormat, setSelectedFormat] = useState<string>('decimal');
  const [decimalPlaces, setDecimalPlaces] = useState<string>('2');
  const [result, setResult] = useState<FormatResult | null>(null);

  const formatNumber = () => {
    const num = parseFloat(inputValue);
    const decimals = parseInt(decimalPlaces) || 2;

    if (!isNaN(num)) {
      let formatted: string;
      let explanation: string;

      switch (selectedFormat) {
        case 'decimal':
          formatted = num.toFixed(decimals);
          explanation = `固定小数位数: ${decimals} 位`;
          break;
        case 'scientific':
          formatted = num.toExponential(decimals);
          explanation = '科学计数法表示';
          break;
        case 'engineering':
          const exp = Math.floor(Math.log10(Math.abs(num)) / 3) * 3;
          const mantissa = num / Math.pow(10, exp);
          formatted = `${mantissa.toFixed(decimals)}e${exp}`;
          explanation = '工程计数法 (3的倍数指数)';
          break;
        case 'thousands':
          formatted = num.toLocaleString('zh-CN', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
          });
          explanation = '千分位分隔符格式';
          break;
        case 'prefix':
          const prefixes = [
            { value: 1e-12, symbol: 'p', name: '皮' },
            { value: 1e-9, symbol: 'n', name: '纳' },
            { value: 1e-6, symbol: 'μ', name: '微' },
            { value: 1e-3, symbol: 'm', name: '毫' },
            { value: 1, symbol: '', name: '' },
            { value: 1e3, symbol: 'k', name: '千' },
            { value: 1e6, symbol: 'M', name: '兆' },
            { value: 1e9, symbol: 'G', name: '吉' },
            { value: 1e12, symbol: 'T', name: '太' },
          ];

          const absNum = Math.abs(num);
          const prefix =
            prefixes.find(p => absNum >= p.value && absNum < p.value * 1000) ||
            prefixes[prefixes.length - 1];
          const prefixedValue = num / prefix.value;
          formatted = `${prefixedValue.toFixed(decimals)} ${prefix.symbol}`;
          explanation = `SI前缀格式: ${prefix.name}${prefix.symbol ? prefix.symbol : ''}`;
          break;
        case 'fraction':
          const tolerance = Math.pow(10, -decimals);
          const fraction = toFraction(num, tolerance);
          formatted = fraction;
          explanation = '分数近似表示';
          break;
        default:
          formatted = num.toString();
          explanation = '原始数值';
      }

      setResult({ formatted, explanation });
    }
  };

  const toFraction = (decimal: number, tolerance: number): string => {
    const numerator = Math.round(decimal / tolerance);
    const denominator = Math.round(1 / tolerance);

    const gcd = (a: number, b: number): number => {
      return b === 0 ? a : gcd(b, a % b);
    };

    const divisor = gcd(numerator, denominator);
    const simplifiedNum = numerator / divisor;
    const simplifiedDen = denominator / divisor;

    if (simplifiedDen === 1) {
      return simplifiedNum.toString();
    }

    return `${simplifiedNum}/${simplifiedDen}`;
  };

  const formatUnits = () => {
    const num = parseFloat(inputValue);

    if (!isNaN(num)) {
      const conversions = [
        { from: 'm', to: 'mm', factor: 1000, name: '米 → 毫米' },
        { from: 'mm', to: 'm', factor: 0.001, name: '毫米 → 米' },
        { from: 'm²', to: 'mm²', factor: 1000000, name: '平方米 → 平方毫米' },
        { from: 'MPa', to: 'Pa', factor: 1000000, name: '兆帕 → 帕斯卡' },
        { from: 'kg', to: 'g', factor: 1000, name: '千克 → 克' },
        { from: 'N', to: 'kN', factor: 0.001, name: '牛顿 → 千牛' },
      ];

      const results = conversions.map(conv => ({
        name: conv.name,
        value: (num * conv.factor).toExponential(3),
      }));

      setResult({
        formatted: results.map(r => `${r.name}: ${r.value}`).join('\n'),
        explanation: '常用工程单位转换',
      });
    }
  };

  const handleFormat = () => {
    if (selectedFormat === 'units') {
      formatUnits();
    } else {
      formatNumber();
    }
  };

  return (
    <div className="formatter">
      <NavigationBar title="格式化工具" />

      <div className="format-section">
        <div className="input-group">
          <label>
            输入数值:
            <input
              type="number"
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              placeholder="输入要格式化的数值"
              step="any"
            />
          </label>
        </div>

        <div className="input-group">
          <label>
            格式化类型:
            <select
              value={selectedFormat}
              onChange={e => setSelectedFormat(e.target.value)}
            >
              <option value="decimal">固定小数位</option>
              <option value="scientific">科学计数法</option>
              <option value="engineering">工程计数法</option>
              <option value="thousands">千分位分隔</option>
              <option value="prefix">SI前缀</option>
              <option value="fraction">分数表示</option>
              <option value="units">单位转换</option>
            </select>
          </label>
        </div>

        {selectedFormat !== 'units' && (
          <div className="input-group">
            <label>
              小数位数:
              <input
                type="number"
                value={decimalPlaces}
                onChange={e => setDecimalPlaces(e.target.value)}
                placeholder="小数位数"
                min="0"
                max="10"
              />
            </label>
          </div>
        )}

        <button onClick={handleFormat} className="format-btn">
          格式化
        </button>

        {result && (
          <div className="result">
            <h3>格式化结果</h3>
            <div className="result-content">
              <div className="formatted-value">
                {selectedFormat === 'units' ? (
                  <pre>{result.formatted}</pre>
                ) : (
                  <div className="value-display">{result.formatted}</div>
                )}
              </div>
              <div className="explanation">
                <strong>说明:</strong> {result.explanation}
              </div>
            </div>
          </div>
        )}

        <div className="examples-section">
          <h4>格式化示例</h4>
          <div className="example-grid">
            <div className="example-card">
              <h5>固定小数位</h5>
              <p>1234.5678 → 1234.57</p>
            </div>
            <div className="example-card">
              <h5>科学计数法</h5>
              <p>1234.5678 → 1.23e+3</p>
            </div>
            <div className="example-card">
              <h5>SI前缀</h5>
              <p>1234.5678 → 1.23 k</p>
            </div>
            <div className="example-card">
              <h5>千分位分隔</h5>
              <p>1234567.89 → 1,234,567.89</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Formatter;
